// Per spec/components.md §2. **P-037.9 (D-109)**: smaller sub-section
// heading used inside or between larger sections (state callouts, minor
// dividers). Differs from `SectionHeader` in three ways:
//
// 1. **Rule is padded 30 px L/R** (not full-width) so the divider visually
//    nests inside the 30 px content gutter rather than running edge-to-edge.
// 2. **Heading typography: 14 px Helvetica bold `#666666`** (`<h3>`) — much
//    smaller than SectionHeader's 21 px / `#000000`.
// 3. **Optional state-flag** rendered immediately left of the heading. The
//    Block layer looks up the flag URL from the state registry and passes
//    it via `flagImageUrl`; this Component is registry-agnostic.
//
// Layout (unchanged 50/30/16 vertical rhythm matching SectionHeader so the
// two are visually consistent):
//
//   ─────  rule, 30 px L/R padded
//          (50 px gap below)
//   [flag] Sub Heading                            [CTA]   ← heading row, 30 px L/R
//                                                          (16 px below before block body)
//
// Props use a discriminated union (D-033 paired-required CTA) at the
// Component layer; the `sub-section-heading` Block (D-109) relaxes the
// CTA to independently optional and forwards atomic CTA only when both
// values are present. Mirrors the SectionHeader / section-heading split.
//
// CTA anchor and flag image are hand-rolled inline (no Link primitive
// size param; flag is a plain `<img>` with width/height/alt). Same
// pattern as SectionHeader's CTA arm.

import { html, toAbsoluteUrl } from '../render'
import { tokens } from '../tokens'
import { HtmlString } from '../types'

interface SubSectionHeaderBase {
  heading: string
  /** Optional state-flag URL. When present, renders a 40×24 px flag image
   *  immediately to the left of the heading text. */
  flagImageUrl?: string
  /** Override the default 30 px L/R padding on the divider + heading rows.
   *  Pass `0` when SubSectionHeader is embedded inside a parent that already
   *  carries its own horizontal padding (e.g., chapter-whatsapp's 190 px
   *  grid cell — P-037.17 D-117). Default `30` matches the system-wide
   *  content-gutter convention. */
  padX?: number
  /** Template-level link color from `RenderContext.linkColor` (P-037.18
   *  D-118). Used as the CTA anchor color when the CTA arm renders.
   *  Defaults to `tokens.color['link-default']`. */
  linkColor?: string
  /** Hide the divider rule (suppress its `<tr>` row entirely). Default
   *  `false` keeps the rule rendered. Independent of `noTopPad` — set
   *  both `true` to render with no rule AND no top padding (heading
   *  text sits flush against the parent edge); set ONLY `hideTopRule`
   *  to drop the rule but KEEP the `PAD_TOP` 30 px gap above the
   *  heading text (P-037.30 ninth post-Phase-Close §C — Banks's
   *  preview-walk: when `big3-signature-events` is the predecessor,
   *  the tinted footer edge visually replaces the rule but the heading
   *  text still needs the 30 px gap so it doesn't sit flush against
   *  the tinted bg). Supersedes the prior `noTopRule` boolean which
   *  bundled both behaviors. */
  hideTopRule?: boolean
  /** Drop the heading row's top padding (`PAD_TOP`, 30 px). Default
   *  `false` keeps the 30 px gap. Used by `big3-signature-events` for
   *  its FIRST baked SubSectionHeader ("Big 3 Meetings") so the
   *  heading sits flush against the Block's internal 20 px top pad
   *  (the per-grid padding-top added in D-136). Independent of
   *  `hideTopRule` — see that prop for the orthogonal semantic. The
   *  pair `{hideTopRule: true, noTopPad: true}` matches the old
   *  `noTopRule: true` behavior exactly. */
  noTopPad?: boolean
}

export type SubSectionHeaderProps =
  | (SubSectionHeaderBase & { ctaLabel?: undefined; ctaUrl?: undefined })
  | (SubSectionHeaderBase & { ctaLabel: string; ctaUrl: string })

const HEADING_SIZE_PX = 14
const HEADING_COLOR = '#666666'
const DEFAULT_PAD_X = 30
// P-037.12: heading-row top padding tightened from 50 → 30 px per Banks's
// audit ("The padding above the sub section headline should be 30 px
// regardless of flag on/off"). SectionHeader keeps its 50 px top padding —
// only SubSectionHeader tightens. Both arms (with-flag + without-flag) use
// the same constant, so the change applies uniformly.
const PAD_TOP = 30
// P-037.29 D-137 §item 2 — `PAD_BOTTOM` dropped from 16 → 0. The
// system-level inter-Block spacer owns the gap from the heading text
// bottom to the next Block — 20 px for sub-section-heading per
// Banks's convention. `big3-signature-events`'s baked SubSectionHeader
// consumers compensate via a per-grid `padding-top: 20px` (no system
// spacer between baked Components inside a single Block).
const PAD_BOTTOM = 0
// Flag dimensions are FIXED per Banks's PSD audit (P-037.11): 40 × 24 px.
// (Was 24 × 16 px from P-037.9 — adjusted up to match the source design.)
const FLAG_WIDTH = 40
const FLAG_HEIGHT = 24
const FLAG_RIGHT_PAD = 8
const CTA_SIZE_PX = 14

export function subSectionHeader(props: SubSectionHeaderProps): HtmlString {
  const padX = props.padX ?? DEFAULT_PAD_X
  // P-037.30 ninth post-Phase-Close §C — split the prior `noTopRule`
  // boolean into two orthogonal flags. `hideTopRule` suppresses the
  // divider <tr>; `noTopPad` drops the heading row's PAD_TOP. The
  // pair `{hideTopRule: true, noTopPad: true}` reproduces the old
  // `noTopRule: true` shape exactly — used by `big3-signature-events`
  // for the baked "Big 3 Meetings" header. `{hideTopRule: true}` only
  // is the new "drop rule, keep 30 px breathing room above text"
  // shape — used by `sub-section-heading.ts` when preceded by
  // `big3-signature-events` (the tinted footer edge visually replaces
  // the rule but the heading text still needs the gap).
  const hideTopRule = props.hideTopRule ?? false
  const noTopPad = props.noTopPad ?? false
  const padTop = noTopPad ? 0 : PAD_TOP
  // h3 semantic level — sub-section nests below the surrounding h2. Hand-
  // rolled (not via the `heading` primitive) because the SubSection heading
  // uses `text-transform: uppercase` per Banks's P-037.11 audit — the
  // primitive doesn't expose that CSS property, and adding a prop just for
  // this one consumer crosses the 3-use threshold (D-067 / D-072 / D-077 /
  // D-094). Data stays mixed-case in the form / persisted state; the
  // uppercase rendering is CSS-only.
  // prettier-ignore
  const headingHtml = html`<h3 style="margin: 0; font-family: ${tokens.type.fontFamily}; font-size: ${HEADING_SIZE_PX}px; font-weight: ${tokens.type.weight.bold}; line-height: ${tokens.type.leading.tight}; color: ${HEADING_COLOR}; text-align: left; text-transform: uppercase;">${props.heading}</h3>`

  // Flag + heading horizontal layout uses a nested table (email-client-
  // safe; works in Outlook Word renderer). When no flag, the headingHtml
  // stands alone in the cell. P-037.26 D-134: flag src threads through
  // `toAbsoluteUrl` so the rendered HTML carries an absolute URL.
  const headingCellInner =
    props.flagImageUrl !== undefined
      ? // prettier-ignore
        html`<table role="presentation" cellpadding="0" cellspacing="0" border="0"><tr><td valign="middle" style="padding-right: ${FLAG_RIGHT_PAD}px;"><img src="${toAbsoluteUrl(props.flagImageUrl)}" width="${FLAG_WIDTH}" height="${FLAG_HEIGHT}" alt="" style="display: block; border: 0;"></td><td valign="middle">${headingHtml}</td></tr></table>`
      : headingHtml

  if (props.ctaLabel === undefined) {
    // No CTA: outer table is single-column, divider's `<tr><td>` naturally
    // spans the full table width.
    // prettier-ignore
    const dividerHtml = hideTopRule
      ? undefined
      : html`<tr><td style="padding: 0 ${padX}px;"><table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"><tr><td style="border-top: 1px solid #d8dde3; font-size: 0; line-height: 0;">&nbsp;</td></tr></table></td></tr>`
    // prettier-ignore
    return html`<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%">${dividerHtml}<tr><td style="padding: ${padTop}px ${padX}px ${PAD_BOTTOM}px ${padX}px;">${headingCellInner}</td></tr></table>`
  }

  const { ctaLabel, ctaUrl } = props
  const ctaColor = props.linkColor ?? tokens.color['link-default']
  const absoluteCtaUrl = toAbsoluteUrl(ctaUrl)
  // prettier-ignore
  const ctaHtml = html`<a href="${absoluteCtaUrl}" target="_blank" rel="noopener noreferrer" style="font-family: ${tokens.type.fontFamily}; color: ${ctaColor}; font-weight: ${tokens.type.weight.bold}; text-decoration: none;">${ctaLabel}</a>`

  // CTA arm: same colspan="2" fix as SectionHeader (P-037.10). The L/R
  // padding stays on the outer cell so the rule visually nests inside the
  // content gutter; colspan=2 makes that cell span both heading + CTA columns.
  // prettier-ignore
  const dividerWithColspanHtml = hideTopRule
    ? undefined
    : html`<tr><td colspan="2" style="padding: 0 ${padX}px;"><table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"><tr><td style="border-top: 1px solid #d8dde3; font-size: 0; line-height: 0;">&nbsp;</td></tr></table></td></tr>`

  // prettier-ignore
  return html`<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%">${dividerWithColspanHtml}<tr><td style="padding: ${padTop}px 0 ${PAD_BOTTOM}px ${padX}px;" valign="middle">${headingCellInner}</td><td style="padding: ${padTop}px ${padX}px ${PAD_BOTTOM}px 0; font-family: ${tokens.type.fontFamily}; font-size: ${CTA_SIZE_PX}px;" align="right" valign="middle">${ctaHtml}</td></tr></table>`
}
